import React, {
    useEffect, useState, useCallback, useContext
} from 'react';
import PropTypes from 'prop-types';
import { useValidation } from 'gw-portals-validation-react';
import { SgProductService, SgVehicleLookupUtil } from 'saga-modules';
import { SgVehicleLookupService } from 'saga-capabilities';
import _, { toInteger } from 'lodash';
import { ViewModelServiceContext } from 'gw-portals-viewmodel-react';
import { TranslatorContext } from '@jutro/locale';
import { publish } from '@jutro/events';
import ADOBE_TOPICS from 'sg-adobe-analytics/AdobeTopics';
import CommonConstants from 'sg-common-constants';
import SpmConstants from 'sg-spm-constants';
import { events } from 'gw-platform-events-js';
import { SgRegistrationLookup, SgVehicleSummary, SgManualVehicleLookup } from './VehicleLookupStates';
import messages from './SgVehicleLookupCtrl.messages';

function SpmVehicleLookupCtrl(props) {
    const {
        id,
        modelVM,
        updateData,
        onValidate: onValidateParent,
        messageParams,
        setYourCarSectionOpen,
        isCarProp,
        isReadOnly,
        allowLookupOnly,
        shouldCallValidation,
        setShowValueQuestion
    } = props;
    const {
        isComponentValid,
        onValidate
    } = useValidation(id);

    const translator = useContext(TranslatorContext);
    const viewModelService = useContext(ViewModelServiceContext);
    const isCarInsurance = isCarProp || SgProductService.isCar(); // TODO - we should remove those product services from common components, not valid for CEP
    const vehicleModel = modelVM.lobData.privateMotor.coverables.spmVehicle;
    const shouldShowSummary = (vehicleModel.registrationNumber.value || vehicleModel.description.value) && isCarInsurance;
    const [internalShowError, setInternalShowError] = useState(false);
    const [vehicleView, updateVehicleView] = useState({
        view: shouldShowSummary ? SpmConstants.VEHICLE_LOOKUP_VIEWS.VEHICLE_SUMMARY : SpmConstants.VEHICLE_LOOKUP_VIEWS.REGISTRATION_LOOKUP
    });

    useEffect(() => {
        if (onValidateParent) {
            onValidateParent(isComponentValid, id);
        }
    }, [isComponentValid, onValidateParent, id]);

    useEffect(() => {
        if (internalShowError) {
            events.emit(CommonConstants.SHOW_VALIDATION_ERROR_EVENT);
        }
    }, [internalShowError]);

    useEffect(() => {
        const updateInternalError = () => {
            setInternalShowError(true);
        };
        events.on(CommonConstants.SHOW_VALIDATION_ERROR_EVENT, updateInternalError);
        return () => {
            events.removeListener(CommonConstants.SHOW_VALIDATION_ERROR_EVENT, updateInternalError);
        };
    }, []);

    const goToErrorPage = (error) => {
        const err = error;
        err.appErrorCode = CommonConstants.SG_ERROR_CODES.SAGA_WEBSITE_ERROR; // Website error
        if (err.httpStatus === 413) { // DE4561 Track Vehicle look-up customer drop off t=14
            err.appErrorCode = CommonConstants.SG_ERROR_CODES.SAGA_PC_CONNECTION_ERROR;
        }
        throw err;
    };

    const performManualLookups = useCallback((state) => {
        const availableVehicles = {
            makes: [],
            models: [],
            years: [],
            engineCapacities: [],
            abiCars: []
        };
        const makeLookup = SgVehicleLookupService.lookupMakes().then((vehicleMakes) => {
            availableVehicles.makes = [
                {
                    id: 'placeholder',
                    sectionName: '',
                    values: [
                        {
                            id: 'placeholderText',
                            name: translator(messages.PleaseSelect),
                            code: ''
                        }
                    ]
                },
                {
                    id: 'topValues',
                    sectionName: translator(messages.TopMakes),
                    values: vehicleMakes.topMakes
                },
                {
                    id: 'allClaims',
                    sectionName: translator(messages.AllMakes),
                    values: vehicleMakes.allMakes
                }
            ];
        })
            .catch((error) => {
                throw error;
            });
        if (state === 'edit') {
            const modelLookup = SgVehicleLookupService.lookupModels(vehicleModel.make.value)
                .then((vehicleModels) => {
                    availableVehicles.models = _.sortBy(vehicleModels, 'name');
                });
            const yearsLookup = SgVehicleLookupService.lookupYears(vehicleModel.make.value, vehicleModel.model.value)
                .then((vehicleYears) => {
                    availableVehicles.years = vehicleYears;
                });
            const abiLookup = SgVehicleLookupService.autofillBasedOnPartialDto({
                make: vehicleModel.make.value,
                model: vehicleModel.model.value,
                year: vehicleModel.year.value
            }).then((result) => {
                availableVehicles.abiCars = result.abiCars;
            });
            return Promise.all([makeLookup, modelLookup, yearsLookup, abiLookup])
                .then(() => {
                    return availableVehicles;
                }).catch((error) => {
                    goToErrorPage(error);
                });
        }
        return Promise.all([makeLookup])
            .then(() => {
                return availableVehicles;
            }).catch((error) => {
                goToErrorPage(error);
            });
    }, [translator, vehicleModel]);

    const setMotorHomeEngineCapacity = (vehicle, motorHomeEngineCapacities) => {
        const veh = vehicle;
        if (veh.engineCapacity && veh.engineCapacity.value) {
            const filteredSizes = _.filter(motorHomeEngineCapacities,
                (capacity) => toInteger(capacity.code) >= veh.engineCapacity.value);
            veh.motorhomeEngineCapacity.value = _.first(filteredSizes);
        }
    };

    const getMotorhomeValues = useCallback(() => {
        const availableVehicles = {
            makes: [],
            models: [],
            years: [],
            engineCapacities: [],
            abiCars: []
        };
        const DEFAULT_FIRST_MOTOR_HOME_YEAR = 1997;
        const CURRENT_YEAR = new Date().getUTCFullYear();
        const motorhomeYearFromLookup = vehicleModel.year.value;
        const startYear = motorhomeYearFromLookup < DEFAULT_FIRST_MOTOR_HOME_YEAR ? motorhomeYearFromLookup : DEFAULT_FIRST_MOTOR_HOME_YEAR;
        const motorHomeEngineCapacities = viewModelService.productMetadata.get('pc').types
            .getTypelist('MotorhomeEngineCapacity').codes;
        for (let year = CURRENT_YEAR; year >= startYear; year -= 1) {
            availableVehicles.years.push({
                code: year,
                name: year
            });
        }
        _.each(motorHomeEngineCapacities, (engineCapacity) => {
            availableVehicles.engineCapacities.push({
                code: engineCapacity.code,
                name: translator({
                    id: engineCapacity.code,
                    defaultMessage: `${SpmConstants.WEB_MOTOR_HOME_DESCRIPTION_FIELD}${engineCapacity.code}CC`
                })
            });
        });
        setMotorHomeEngineCapacity(vehicleModel, availableVehicles.engineCapacities);
        // Set Motor Home default values for required fields
        vehicleModel.make.value = SpmConstants.WEB_MOTOR_HOME_MAKE_FIELD;
        vehicleModel.model.value = SpmConstants.WEB_MOTOR_HOME_MODEL_FIELD;

        return availableVehicles;
    }, [translator, vehicleModel, viewModelService.productMetadata]);

    const setupAndGoToManualView = useCallback((view, state) => {
        if (isCarInsurance) {
            performManualLookups(state)
                .then((availableVehicles) => {
                    updateVehicleView({ view: view, state: state, availableVehicles: availableVehicles });
                });
        } else {
            const availableVehicles = getMotorhomeValues();
            updateVehicleView({ view: view, state: state, availableVehicles: availableVehicles });
            onValidate(true, id);
            if (setYourCarSectionOpen) {
                setYourCarSectionOpen(true);
            }
        }
    }, [isCarInsurance, performManualLookups, getMotorhomeValues, setYourCarSectionOpen, onValidate, id]);

    const changeVehicleView = useCallback((view, state) => {
        // reset show errors when changing view
        setInternalShowError(false);
        if (view === SpmConstants.VEHICLE_LOOKUP_VIEWS.REGISTRATION_LOOKUP) {
            SgVehicleLookupUtil.clearVehicleLookup(vehicleModel);
            onValidate(false, id);
        }

        if (view === SpmConstants.VEHICLE_LOOKUP_VIEWS.MANUAL_LOOKUP) {
            setupAndGoToManualView(view, state);
        } else {
            if (shouldCallValidation) {
                onValidateParent(true, id);
            }
            publish(ADOBE_TOPICS.VEHICLE_LOOKUP_TRACKING, { vehicleModel });
            updateVehicleView({ view: view });
        }
    }, [id, onValidateParent, setInternalShowError, setupAndGoToManualView, vehicleModel]);

    const handleVehicleValue = useCallback((vehicleInfo) => {
        if (vehicleInfo.experianValue && vehicleInfo.experianValue.amount > 0) {
            vehicleModel.vehicleValue.value = vehicleInfo.experianValue;
            vehicleModel.hasExperianValue.value = true;
        } else {
            SgVehicleLookupUtil.clearVehicleProperties(vehicleModel, ['vehicleValue']);
            SgVehicleLookupUtil.clearVehicleProperties(vehicleModel, ['hasExperianValue']);
        }
    }, [vehicleModel]);

    const commomOverrideProps = {
        modelVM,
        vehicleModel,
        updateData,
        changeVehicleView,
        onValidate,
        showErrors: internalShowError,
        setShowErrors: setInternalShowError,
        messageParams,
        allowLookupOnly,
        isCarInsurance,
        isReadOnly,
        handleVehicleValue
    };

    // render component based on defined view
    const renderVehicleLookupByState = () => {
        if (setShowValueQuestion) {
            setShowValueQuestion(false);
        }
        if (vehicleView.view === SpmConstants.VEHICLE_LOOKUP_VIEWS.REGISTRATION_LOOKUP) {
            return (
                <SgRegistrationLookup
                    id="regLookup"
                    {...commomOverrideProps}
                    checkManualLookups={performManualLookups}
                />
            );
        } if (vehicleView.view === SpmConstants.VEHICLE_LOOKUP_VIEWS.MANUAL_LOOKUP) {
            return (
                <SgManualVehicleLookup
                    id="manualLookup"
                    {...commomOverrideProps}
                    state={vehicleView.state}
                    availableVehicles={vehicleView.availableVehicles}
                />
            );
        } if (vehicleView.view === SpmConstants.VEHICLE_LOOKUP_VIEWS.VEHICLE_SUMMARY) {
            if (setYourCarSectionOpen) {
                setYourCarSectionOpen(true);
            }
            if (setShowValueQuestion) {
                setShowValueQuestion(true);
            }
            return <SgVehicleSummary {...commomOverrideProps} />;
        }
        return <SgRegistrationLookup {...commomOverrideProps} checkManualLookups={performManualLookups} />;
    };

    return renderVehicleLookupByState();
}

SpmVehicleLookupCtrl.propTypes = {
    id: PropTypes.string,
    modelVM: PropTypes.shape({}).isRequired,
    updateData: PropTypes.func.isRequired,
    showErrors: PropTypes.bool,
    onValidate: PropTypes.func.isRequired,
    isCar: PropTypes.bool,
    allowLookupOnly: PropTypes.bool,
    isReadOnly: PropTypes.bool,
    shouldNotCallValidation: PropTypes.bool
};

SpmVehicleLookupCtrl.defaultProps = {
    id: undefined,
    showErrors: undefined,
    isCar: undefined,
    allowLookupOnly: false,
    isReadOnly: false,
    shouldCallValidation: true
};

export default SpmVehicleLookupCtrl;
